import { useQuery } from '@tanstack/react-query';
import type { RichDocument } from '../components/rich-text';
import { api, apiFileUrl, upload } from './api';
import { currentSchool, schoolHref } from './school';
import type { SoftwareOption } from './software';
import { resizeImage } from './images';
import type { Page } from './types';

export type KnowledgeVisibility = 'PUBLIC' | 'STUDENTS' | 'STAFF' | 'INTERNAL' | 'PRIVATE';

export const VISIBILITY_OPTIONS: { value: KnowledgeVisibility; label: string; description: string }[] = [
  { value: 'PUBLIC', label: 'Public', description: 'Anyone, even without signing in. Linked from the sign-in page.' },
  { value: 'STUDENTS', label: 'Students', description: 'Signed-in students.' },
  { value: 'STAFF', label: 'Teachers & staff', description: 'Signed-in teachers and staff.' },
  { value: 'INTERNAL', label: 'Internal only', description: 'The technology team.' },
  { value: 'PRIVATE', label: 'Only me', description: 'Just you - handy for a draft.' },
];

/** Students and teachers & staff can be chosen together; the others are for their audience alone. */
export const EXCLUSIVE_VISIBILITY: KnowledgeVisibility[] = ['PUBLIC', 'INTERNAL', 'PRIVATE'];

/** Who can read an article after ticking or unticking one choice. */
export function toggleVisibility(current: KnowledgeVisibility[], value: KnowledgeVisibility): KnowledgeVisibility[] {
  if (current.includes(value)) return current.filter((other) => other !== value);
  if (EXCLUSIVE_VISIBILITY.includes(value)) return [value];
  return VISIBILITY_OPTIONS.map((option) => option.value).filter((other) => other === value || (current.includes(other) && !EXCLUSIVE_VISIBILITY.includes(other)));
}

export const VISIBILITY_LABEL = Object.fromEntries(VISIBILITY_OPTIONS.map((option) => [option.value, option.label])) as Record<KnowledgeVisibility, string>;

export const VISIBILITY_TONE: Record<KnowledgeVisibility, 'green' | 'blue' | 'violet' | 'amber' | 'gray'> = {
  PUBLIC: 'green',
  STUDENTS: 'blue',
  STAFF: 'violet',
  INTERNAL: 'amber',
  PRIVATE: 'gray',
};

export interface KnowledgeModelTag {
  id: string;
  /** Manufacturer and model. */
  name: string;
  imageUrl: string | null;
}

export interface KnowledgeArticleSummary {
  id: string;
  title: string;
  summary: string | null;
  excerpt: string;
  /** Who can read it: one of public, internal, or private, or students and/or teachers and staff. */
  visibility: KnowledgeVisibility[];
  software: Pick<SoftwareOption, 'id' | 'name'>[];
  topics: string[];
  models: KnowledgeModelTag[];
  /** Null on public pages. */
  authorName: string | null;
  updatedByName: string | null;
  createdAt: string;
  updatedAt: string;
  /** Signed in: whether the reader wrote it. */
  mine?: boolean;
}

export interface KnowledgeArticle extends KnowledgeArticleSummary {
  body: RichDocument;
  /** Signed in only. */
  permissions?: { edit: boolean; delete: boolean };
}

export interface KnowledgePage extends Page<KnowledgeArticleSummary> {
  canWrite?: boolean;
}

export interface KnowledgeTagCounts {
  models: (KnowledgeModelTag & { count: number })[];
  software: { id: string; name: string; count: number }[];
  topics: { value: string; count: number }[];
}

/** What a form offers help articles about: a device's model, or a piece of software. Software wins when both are given. */
export interface KnowledgeSubject {
  modelId?: string | null;
  softwareId?: string | null;
}

/** How many articles a form offers before sending people to the whole knowledgebase. */
export const MAX_SUGGESTED_ARTICLES = 4;

/** How the knowledgebase is filtered for a subject - the same filters its own list page takes. */
const subjectFilter = (subject: KnowledgeSubject): { key: 'softwareId' | 'modelId'; value: string } | null =>
  subject.softwareId ? { key: 'softwareId', value: subject.softwareId } : subject.modelId ? { key: 'modelId', value: subject.modelId } : null;

/**
 * The newest articles tagged with a device model or a piece of software, for the forms that offer them. The server only
 * ever returns articles the reader may see, so a requester is never shown the team's internal notes.
 */
export function useArticlesFor(subject: KnowledgeSubject, enabled = true) {
  const filter = subjectFilter(subject);
  return useQuery({
    queryKey: ['knowledge', 'for', filter?.key ?? '', filter?.value ?? ''],
    queryFn: () => api.get<KnowledgePage>('/knowledge', { ...(filter ? { [filter.key]: filter.value } : {}), pageSize: MAX_SUGGESTED_ARTICLES }),
    enabled: enabled && !!filter,
    staleTime: 60_000,
  });
}

/** An in-app knowledgebase address, built whole so it can open in its own tab from inside a form. */
const schoolPage = (path: string) => (currentSchool ? schoolHref(currentSchool, path) : path);

/** The knowledgebase, showing what a subject is tagged with. */
export const knowledgeListHref = (subject: KnowledgeSubject = {}) => {
  const filter = subjectFilter(subject);
  return schoolPage(`/knowledge${filter ? `?${filter.key}=${encodeURIComponent(filter.value)}` : ''}`);
};

export const knowledgeArticleHref = (id: string) => schoolPage(`/knowledge/${id}`);

/** How many articles the signed-in person can read, and whether they can write them. */
export const useKnowledgeSummary = (enabled = true) =>
  useQuery({
    queryKey: ['knowledge', 'summary'],
    queryFn: () => api.get<{ count: number; canWrite: boolean }>('/knowledge/summary'),
    enabled,
    staleTime: 60_000,
  });

export const knowledgeImageUrl = (imageId: string) => apiFileUrl(`/api/knowledge/images/${imageId}`);

export const publicKnowledgeImageUrl = (slug: string, imageId: string) => `/api/public/schools/${slug}/knowledge/images/${imageId}`;

/** Resizes a picture for an article (at most 1600 pixels across and 3 MB) and uploads it. */
export async function uploadKnowledgeImage(file: File) {
  const { blob, width, height } = await resizeImage(file, { maxSide: 1600, maxBytes: 3 * 1024 * 1024, quality: 0.9 });
  return upload<{ id: string; width: number; height: number }>('/knowledge/images', blob, {
    'x-picture': encodeURIComponent(JSON.stringify({ fileName: file.name.slice(0, 255), width, height })),
  });
}
